import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box, Container, Typography, Button, Alert, Chip, Stack,
  Fade, Slide, Collapse, Card, CardContent, Tooltip
} from '@mui/material';
import {
  Error, Home, ArrowBack, BugReport, NetworkCheck,
  Security, Search, ExpandMore, ExpandLess, ContentCopy,Timer
} from '@mui/icons-material';
import CustomCard from '../../components/common/CustomCard';
import CustomSnackbar from "../../components/common/CustomSnackbar";

const ERROR_CONFIGS = {
  network: {
    icon: NetworkCheck,
    title: 'Connection Problem',
    description: 'Unable to connect to our servers. Please check your internet connection.',
    color: 'warning',
    suggestions: ['Check your internet', 'Disable VPN if active']
  },
  server: {
    icon: Error,
    title: 'Server Error',
    description: 'Our servers are experiencing issues. Our team has been notified.',
    color: 'error',
    suggestions: ['Try again later', 'Contact support']
  },
  unauthorized: {
    icon: Security,
    title: 'Access Denied',
    description: 'You don’t have permission to access this resource.',
    color: 'error',
    suggestions: ['Verify credentials', 'Request access permissions']
  },
  link_expired: {
    icon: Timer,
    title: 'Interview Link Expired',
    description: 'The interview link you are trying to access is no longer valid or has expired.',
    color: 'warning',
    suggestions: [
      'Interview links have a limited validity period',
      'Request a new interview link from your recruiter',
      'Check your email for a new link'
    ],
    backgroundColor: '#fff3e0',
    borderColor: '#fb8c00'
  },
  notFound: {
    icon: Search,
    title: 'Page Not Found',
    description: 'The page you’re looking for doesn’t exist or has been moved.',
    color: 'info',
    suggestions: ['Check URL spelling', 'Go back to previous page']
  },
  generic: {
    icon: Error,
    title: 'Unexpected Error',
    description: 'We encountered an unexpected error. Please try again later.',
    color: 'error',
    suggestions: ['Clear cache', 'Try again later']
  }
};

const iconWrapperStyle = (color) => ({
  width: 100,
  height: 100,
  borderRadius: '50%',
  backgroundColor: `${color}.light`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: '0 auto 24px',
  boxShadow: `0 12px 40px rgba(0,0,0,0.15)`
});

export default function ErrorPage() {
  const location = useLocation();
  const navigate = useNavigate();

  const errorState = useMemo(() => ({
    errorType: location.state?.errorType || 'generic',
    errorMessage: location.state?.message || 'Something went wrong',
    errorCode: location.state?.code || '500',
    errorDetails: location.state?.details || null,
    stackTrace: location.state?.stackTrace || null,
    showHomeButton: location.state?.showHomeButton !== false,
    timestamp: location.state?.timestamp || new Date().toISOString()
  }), [location.state]);

  const config = ERROR_CONFIGS[errorState.errorType] || ERROR_CONFIGS.generic;
  const IconComponent = config.icon;

  const [showDetails, setShowDetails] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });

  useEffect(() => {
    setSnackbar({
      open: true,
      message: `Error ${errorState.errorCode}: ${config.title}`,
      severity: 'error'
    });
  }, [errorState.errorCode, config.title]);

  const handleGoHome = useCallback(() => navigate('/', { replace: true }), [navigate]);
  const handleGoBack = useCallback(() => {
    if (window.history.length > 1) navigate(-1);
    else navigate('/', { replace: true });
  }, [navigate]);

  const handleCopyError = useCallback(() => {
    const errorInfo = {
      code: errorState.errorCode,
      type: errorState.errorType,
      message: errorState.errorMessage,
      timestamp: errorState.timestamp,
      url: window.location.href,
      userAgent: navigator.userAgent
    };
    navigator.clipboard.writeText(JSON.stringify(errorInfo, null, 2)).then(() => {
      setSnackbar({ open: true, message: 'Error details copied', severity: 'success' });
    });
  }, [errorState]);

  const handleReportError = useCallback(() => {
    setSnackbar({ open: true, message: 'Error report sent', severity: 'success' });
  }, []);

  return (
    <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      p: 2
    }}>
      <Container maxWidth="lg">
        <Fade in timeout={1000}>
          <CustomCard elevation={12} sx={{ height: "100%", width: "100%" }}>
            <Box sx={{ p: 4, textAlign: 'center' }}>

              <Slide direction="down" in timeout={800}>
                <Box sx={iconWrapperStyle(config.color)}>
                  <IconComponent sx={{ fontSize: 50, color: `${config.color}.dark` }} />
                </Box>
              </Slide>

              <Typography variant="h3" fontWeight={800}>{config.title}</Typography>
              {/* <Chip label={`Error ${errorState.errorCode}`} color={config.color} variant="outlined" sx={{ mb: 2 }} /> */}
              <Typography variant="h6" color="text.secondary" sx={{ mb: 3, maxWidth: 600, mx: 'auto' }}>
                {config.description}
              </Typography>


              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom color="primary">💡 What you can try:</Typography>
                  <Stack spacing={1}>
                    {config.suggestions.map((s, i) => (
                      <Typography key={i} variant="body2" color="text.secondary">
                        • {s}
                      </Typography>
                    ))}
                  </Stack>
                </CardContent>
              </Card>

              <Stack direction="row" spacing={2} justifyContent="center" sx={{ mb: 3 }}>
                {errorState.showHomeButton && (
                  <Button variant="outlined" size="large" startIcon={<Home />} onClick={handleGoHome}>
                    Go Home
                  </Button>
                )}
                <Tooltip title="Go back"><Button variant="text" startIcon={<ArrowBack />} onClick={handleGoBack}>Go Back</Button></Tooltip>
                <Tooltip title="Report this error"><Button variant="text" startIcon={<BugReport />} onClick={handleReportError}>Report</Button></Tooltip>
                <Tooltip title="Copy error details"><Button variant="text" startIcon={<ContentCopy />} onClick={handleCopyError}>Copy Details</Button></Tooltip>
              </Stack> 

              {(errorState.errorDetails || errorState.stackTrace) && (
                <>
                  <Button
                    variant="text"
                    startIcon={showDetails ? <ExpandLess /> : <ExpandMore />}
                    onClick={() => setShowDetails((prev) => !prev)}
                    sx={{ mb: 2 }}
                  >
                    Technical Details
                  </Button>

                  <Collapse in={showDetails}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="subtitle2" gutterBottom>Error Details:</Typography>
                        <Typography variant="body2" component="pre"
                          sx={{ fontFamily: 'monospace', fontSize: '0.8rem', overflow: 'auto' }}>
                          {JSON.stringify({
                            code: errorState.errorCode,
                            type: errorState.errorType,
                            message: errorState.errorMessage,
                            timestamp: errorState.timestamp,
                            details: errorState.errorDetails,
                            stackTrace: errorState.stackTrace
                          }, null, 2)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Collapse>
                </>
              )}
            </Box>
          </CustomCard>
        </Fade>
      </Container>

      <CustomSnackbar
        open={snackbar.open}
        message={snackbar.message}
        type={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        autoHideDuration={6000}
      />
    </Box>
  );
}
